/**
 * Pro CRM data hooks — the single seam between the preview fixtures and the
 * live `/api/professional/*` endpoints.
 *
 *   PREVIEW (no Clerk key, dev/preview route):
 *     return the hand-authored fixtures, with a tiny simulated delay so loading
 *     states are still exercised.
 *
 *   PROD (Clerk key present):
 *     fetch the real DTOs with a session token and run them through the
 *     wire→view-model adapters (lib/adapt.ts).
 *
 * Both paths resolve to the SAME view-model shapes (lib/view-model.ts), so the
 * components never branch on environment. Every hook exposes the four standard
 * states: loading / error / empty / data.
 *
 * State is a single reducer so the load effect never calls setState
 * synchronously in its body (React 19 `react-hooks/set-state-in-effect`): the
 * effect kicks off an async `run()` that dispatches once it has actually
 * started awaiting. The token resolver is captured in a ref updated inside an
 * effect (never during render).
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import {
  apiRequest,
  ApiError,
  deleteJSON,
  getJSON,
  patchJSON,
  postJSON,
  putJSON,
} from "../lib/api";
import { IS_PREVIEW, previewQueryParam } from "../lib/pro-mode";
import { resolvePreviewPeople } from "../lib/people-preview";
import {
  PRO_RESOURCES_DIRTY_EVENT,
  type ProResourcesDirtyDetail,
} from "../lib/pro-resource-dirty";
import { deriveKpis, deriveSignals, deriveSuggestion } from "../lib/derive";
import { composeProUser, readClerkIdentity } from "../lib/pro-identity";
import { useProfileStore } from "../stores/profile";
import { useProToken, type GetProToken } from "./useProToken";
import type { RevertReasonCode, Stage } from "../lib/stage";
import {
  adaptBillingStatus,
  adaptClientDetail,
  adaptContact,
  adaptFeedItem,
  adaptImportPreview,
  adaptImportResult,
  adaptInviteResult,
  adaptNotificationPrefs,
  adaptProProfile,
  adaptRateCard,
  adaptPersonRow,
  personRowToClientVM,
  personRowToContactVM,
} from "../lib/adapt";
import {
  FIXTURE_ACTIVITY_FEED,
  FIXTURE_ASSISTANT_ALLOWANCE_FREE,
  FIXTURE_ASSISTANT_ALLOWANCE_FREE_EXHAUSTED,
  FIXTURE_ASSISTANT_ALLOWANCE_PRO,
  FIXTURE_BILLING_STATUS,
  FIXTURE_BILLING_STATUS_CARD_ON_FILE,
  FIXTURE_BILLING_STATUS_EXPIRED,
  FIXTURE_CLIENTS,
  FIXTURE_CONTACT_FUNNEL,
  FIXTURE_CONTACTS,
  FIXTURE_CRM_BILLING_CANCEL_AT_PERIOD_END,
  FIXTURE_CRM_BILLING_CANCELED,
  FIXTURE_CRM_BILLING_FREE,
  FIXTURE_CRM_BILLING_PAST_DUE,
  FIXTURE_CRM_BILLING_PRO,
  FIXTURE_IMPORT_PREVIEW,
  FIXTURE_KPIS,
  FIXTURE_NOTIFICATION_PREFS,
  FIXTURE_PRO,
  FIXTURE_PRO_PROFILE,
  FIXTURE_PRO_REFERRALS,
  FIXTURE_PEOPLE,
  FIXTURE_RATE_CARD,
  FIXTURE_SIGNALS,
  FIXTURE_SUGGESTION,
} from "../lib/fixtures";
import type {
  AgreementActionRequest,
  AgreementActionResponse,
  ArchiveContactRequest,
  BatchInviteFailedEntry,
  BatchInviteJobStatus,
  BatchInviteStartResult,
  BillingStatus,
  Capability,
  CommitMappingBody,
  CreateContactRequest,
  Enrichment,
  ImportCommitResult,
  ImportPreview,
  InvitePreflightResult,
  InviteSendResult,
  NotificationPref,
  NotificationPrefsResponse,
  MyReferralsResponse,
  ProActivityEvent,
  ProClientDetail,
  ProClientNotesResponse,
  ProClientsQuery,
  ProContactDTO,
  ProEventsResponse,
  ProProfile,
  ProPeopleListResponse,
  ProPeopleQuery,
  ProPersonActivityResponse,
  ProPersonDetailResponse,
  ProRateCardResponse,
  SetupIntentResponse,
  StageActionRequest,
  StageOutcome,
  UpdateContactRequest,
  UpdateProProfileRequest,
} from "../lib/types";
import type {
  ActivityFeedItem,
  AssistantAllowanceVM,
  BatchFailedEntryVM,
  BatchJobVM,
  BillingStatusVM,
  CRMBillingSummaryVM,
  ClientVM,
  ContactFunnelSummaryVM,
  ContactVM,
  ImportPreviewVM,
  ImportResultVM,
  InviteResultVM,
  KpiMetric,
  NotificationPrefVM,
  PreflightResultVM,
  ProProfileVM,
  ProUserResult,
  PersonVM,
  RateCardRowVM,
  SignalEvent,
  SillSuggestion,
} from "../lib/view-model";

const PREVIEW_DELAY_MS = 220;

/** Standard async result envelope shared by every hook. */
export interface AsyncResult<T> {
  data: T | null;
  /** The request key that produced `data`. Consumers changing identity-bearing
   * route keys must reject a payload from the prior key during the render
   * before the replacement effect has a chance to dispatch. */
  dataKey?: string;
  /** True only while loading with no data yet (initial load / post-error). */
  isLoading: boolean;
  /**
   * True while a load is in flight but prior data is still mounted. Pages
   * must keep rendering `data` during a refresh — gating a skeleton on this
   * is the full-page flicker this split exists to prevent.
   */
  isRefreshing: boolean;
  error: string | null;
  /**
   * The failed request's `ApiError.code` (e.g. `crm_entitlement_required`),
   * or null when the last load succeeded or failed with a non-ApiError
   * (network/parse) error. Lets a page branch on the machine-readable denial
   * code (SILL-521 M2 cluster 4 locked client-detail) without re-parsing
   * `error`, which may be a generic fallback sentence.
   */
  errorCode: string | null;
  /** True once a request settled with no rows (data === empty). */
  isEmpty: boolean;
  /**
   * Triggers a reload and returns a promise that resolves once the
   * resulting load settles (success or failure) — a caller that needs to
   * resync before propagating a downstream error (e.g. ClientDetailPage's
   * stage-transition try/finally) can `await refresh()` instead of firing
   * it and moving on. A caller that doesn't care about completion can still
   * call `refresh()` without awaiting — the returned promise is ignorable.
   */
  refresh: () => Promise<void>;
}

// ─────────────────────────────────────────────────────────────────────────
// Async reducer — shared by the fetch hooks.
// ─────────────────────────────────────────────────────────────────────────

interface AsyncState<T> {
  data: T | null;
  dataKey: string;
  isLoading: boolean;
  isRefreshing: boolean;
  error: string | null;
  errorCode: string | null;
}

type AsyncAction<T> =
  | { kind: "start" }
  | { kind: "success"; data: T | null; dataKey: string }
  | { kind: "failure"; error: string; errorCode?: string | null };

function asyncReducer<T>(
  state: AsyncState<T>,
  action: AsyncAction<T>,
): AsyncState<T> {
  switch (action.kind) {
    case "start":
      // A load with data already mounted is a refresh: keep the data visible
      // and do NOT flip isLoading (pages gate skeletons on it).
      return {
        ...state,
        isLoading: state.data === null,
        isRefreshing: state.data !== null,
        error: null,
        errorCode: null,
      };
    case "success":
      return {
        data: action.data,
        dataKey: action.dataKey,
        isLoading: false,
        isRefreshing: false,
        error: null,
        errorCode: null,
      };
    case "failure":
      return {
        data: null,
        dataKey: "",
        isLoading: false,
        isRefreshing: false,
        error: action.error,
        errorCode: action.errorCode ?? null,
      };
    default:
      return state;
  }
}

const INITIAL: AsyncState<never> = {
  data: null,
  dataKey: "",
  isLoading: true,
  isRefreshing: false,
  error: null,
  errorCode: null,
};

/**
 * Shared fetch hook. `key` re-triggers the load when it changes; `loader`
 * resolves the data given a token resolver + abort signal. The loader is
 * captured in a ref so its identity doesn't retrigger the effect.
 */
function useAsync<T>(
  key: string,
  loader: (getToken: GetProToken, signal: AbortSignal) => Promise<T | null>,
  tokenResolver?: GetProToken,
): AsyncResult<T> {
  const defaultGetToken = useProToken();
  const getToken = tokenResolver ?? defaultGetToken;
  const [state, dispatch] = useReducer(
    asyncReducer<T>,
    INITIAL as unknown as AsyncState<T>,
  );
  const [nonce, setNonce] = useReducer((n: number) => n + 1, 0);

  // Refs updated inside an effect (never during render) so the load effect can
  // read the latest token resolver + loader without depending on their
  // identities (which would refetch on every parent re-render).
  const getTokenRef = useRef(getToken);
  const loaderRef = useRef(loader);
  useEffect(() => {
    getTokenRef.current = getToken;
    loaderRef.current = loader;
  }, [getToken, loader]);

  // Resolvers queued by refresh() calls, flushed once the NEXT load settles
  // (success or failure) — see the `refresh` doc comment on AsyncResult.
  // Persisted in a ref (not per-effect state) so a resolver queued just
  // before an in-flight load gets cancelled/re-triggered is still flushed
  // by whichever subsequent run() actually completes.
  const pendingRefreshResolvers = useRef<Array<() => void>>([]);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    async function run() {
      dispatch({ kind: "start" });
      try {
        const data = await loaderRef.current(
          getTokenRef.current,
          controller.signal,
        );
        if (!cancelled) dispatch({ kind: "success", data, dataKey: key });
      } catch (err) {
        if (cancelled || controller.signal.aborted) return;
        dispatch({
          kind: "failure",
          error: errorMessage(err),
          errorCode: err instanceof ApiError ? (err.code ?? null) : null,
        });
      } finally {
        if (!cancelled) {
          const resolvers = pendingRefreshResolvers.current;
          pendingRefreshResolvers.current = [];
          resolvers.forEach((resolve) => resolve());
        }
      }
    }

    void run();
    return () => {
      cancelled = true;
      controller.abort();
    };
    // key + nonce drive (re)loads; the loader/token are read via refs.
  }, [key, nonce]);

  const refresh = useCallback(() => {
    return new Promise<void>((resolve) => {
      pendingRefreshResolvers.current.push(resolve);
      setNonce();
    });
  }, []);

  return {
    data: state.data,
    dataKey: state.dataKey,
    isLoading: state.isLoading,
    isRefreshing: state.isRefreshing,
    error: state.error,
    errorCode: state.errorCode,
    isEmpty: !state.isLoading && !state.error && isEmptyData(state.data),
    refresh,
  };
}

function useDirtyResourceRefresh(resources: string[], refresh: () => void) {
  const resourceKey = resources.filter(Boolean).join("\n");

  useEffect(() => {
    if (typeof window === "undefined" || resourceKey === "") return;
    const watched = new Set(resourceKey.split("\n"));
    const onDirty = (event: Event) => {
      const detail = (event as CustomEvent<ProResourcesDirtyDetail>).detail;
      if (!detail?.resources?.some((resource) => watched.has(resource))) {
        return;
      }
      refresh();
    };

    window.addEventListener(PRO_RESOURCES_DIRTY_EVENT, onDirty);
    return () => window.removeEventListener(PRO_RESOURCES_DIRTY_EVENT, onDirty);
  }, [refresh, resourceKey]);
}

function isEmptyData(data: unknown): boolean {
  if (data === null || data === undefined) return true;
  if (Array.isArray(data)) return data.length === 0;
  return false;
}

// ─────────────────────────────────────────────────────────────────────────
// useClients — the roster.
// ─────────────────────────────────────────────────────────────────────────

type CursorQuery = { cursor?: string };
type CursorPage = { next_cursor?: string };

async function loadCursorPages<
  TPage extends CursorPage,
  TQuery extends CursorQuery,
>(
  initialQuery: TQuery,
  buildPath: (query: TQuery) => string,
  getToken: GetProToken,
  signal: AbortSignal,
): Promise<TPage[]> {
  const token = (await getToken()) ?? undefined;
  const pages: TPage[] = [];
  let query = initialQuery;

  for (;;) {
    const page = await getJSON<TPage>(buildPath(query), token, signal);
    pages.push(page);
    if (!page.next_cursor) return pages;
    query = { ...query, cursor: page.next_cursor };
  }
}

// Plan 377 M5 (REST surface, design spec §4.1): GET /api/professional/clients
// (the roster this hook used to read) is retired — People (GET /people) is
// the one roster source now. This maps the legacy ProClientsQuery onto the
// People wire query, fixing sill_status=connected (the roster this hook
// feeds only ever enumerated connected clients) and passing the rest of the
// query through where the two shapes agree (role/q→query/sort/tags→tag).
// funnel_state has no People equivalent (People's status axis is
// sill_status, already fixed to "connected" here) and is intentionally
// dropped — no production call site sets it (grep confirms every
// useClients() caller passes {} or {sort}).
function buildPeopleClientsPath(query: ProClientsQuery): string {
  const params = new URLSearchParams();
  params.append("sill_status", "connected");
  if (query.role) params.set("role", query.role);
  if (query.q) params.set("query", query.q);
  if (query.sort) params.set("sort", query.sort);
  if (query.cursor) params.set("cursor", query.cursor);
  for (const t of query.tags ?? []) {
    if (t) params.append("tag", t);
  }
  const qs = params.toString();
  return `/api/professional/people${qs ? `?${qs}` : ""}`;
}

/**
 * Roster hook. `query` is the SERVER-side filter set (role / q / funnel_state /
 * sort); the table applies its own client-side operational tabs + tier chips on
 * top of the returned set. Sources the connected-only projection of the
 * People roster (plan 377 M5) — see personRowToClientVM's doc comment for
 * what is DEGRADED relative to the retired profile-keyed roster read.
 */
export function useClients(
  query: ProClientsQuery = {},
): AsyncResult<ClientVM[]> {
  const key = JSON.stringify(query);
  const loader = useCallback(
    async (getToken: GetProToken, signal: AbortSignal) => {
      if (IS_PREVIEW) {
        await delay(PREVIEW_DELAY_MS, signal);
        return FIXTURE_CLIENTS;
      }
      const pages = await loadCursorPages<
        ProPeopleListResponse,
        ProClientsQuery
      >(JSON.parse(key) as ProClientsQuery, buildPeopleClientsPath, getToken, signal);
      return pages.flatMap((res) =>
        (res.people ?? [])
          .map((row) => personRowToClientVM(row))
          .filter((c): c is ClientVM => c !== null),
      );
    },
    [key],
  );
  const result = useAsync<ClientVM[]>(key, loader);
  useDirtyResourceRefresh(["clients"], result.refresh);
  return result;
}

function buildPeoplePath(query: ProPeopleQuery): string {
  const params = new URLSearchParams();
  if (query.query) params.set("query", query.query);
  if (query.quick_view) params.set("quick_view", query.quick_view);
  if (query.role) params.set("role", query.role);
  if (query.cursor) params.set("cursor", query.cursor);
  const repeated: Record<string, string[] | undefined> = {
    relationship_stage: query.relationship_stage,
    sill_status: query.sill_status,
    verification_tier: query.verification_tier,
    timeline: query.timeline,
    tag: query.tag,
  };
  for (const [key, values] of Object.entries(repeated)) {
    for (const value of values ?? []) if (value) params.append(key, value);
  }
  const qs = params.toString();
  return `/api/professional/people${qs ? `?${qs}` : ""}`;
}

/** Backend-owned People list: never merge legacy Clients and Contacts in React. */
export type PeopleData = {
  people: PersonVM[];
  counts: ProPeopleListResponse["counts"];
};
export function usePeople(query: ProPeopleQuery = {}): AsyncResult<PeopleData> {
  const key = JSON.stringify(query);
  const loader = useCallback(
    async (getToken: GetProToken, signal: AbortSignal) => {
      if (IS_PREVIEW) {
        await delay(PREVIEW_DELAY_MS, signal);
        return resolvePreviewPeople(
          FIXTURE_PEOPLE,
          JSON.parse(key) as ProPeopleQuery,
        );
      }
      const pages = await loadCursorPages<
        ProPeopleListResponse,
        ProPeopleQuery
      >(JSON.parse(key) as ProPeopleQuery, buildPeoplePath, getToken, signal);
      return {
        people: pages.flatMap((page) =>
          (page.people ?? []).map(adaptPersonRow),
        ),
        counts: pages[0]?.counts ?? {
          all: 0,
          active: 0,
          needs_me: 0,
          ready_soon: 0,
          going_cold: 0,
          archived: 0,
        },
      };
    },
    [key],
  );
  const result = useAsync<PeopleData>(`people:${key}`, loader);
  useDirtyResourceRefresh(["people"], result.refresh);
  return {
    ...result,
    isEmpty:
      !result.isLoading &&
      !result.error &&
      (result.data?.people.length ?? 0) === 0,
  };
}

/** Builds a Capability from the flat PersonVM boolean the DEV preview
 * fixtures carry — allowed=true never carries a reason; allowed=false has
 * no fixture-authored reason to surface, matching the "no reason" shape a
 * real denial-without-a-named-cause would also produce. */
function fixtureCapability(allowed: boolean): Capability {
  return { allowed };
}

/** Builds the {@link Enrichment} discriminated union for the DEV preview
 * fixtures from the flat PersonVM.enrichmentState + an optional resolved
 * connected client (available only). */
function fixtureEnrichment(
  state: PersonVM["enrichmentState"],
  connectedClient: { id: string } | undefined,
): Enrichment {
  if (state === "available" && connectedClient) {
    return { state: "available", data: { profile_id: connectedClient.id } };
  }
  if (state === "available") {
    // No resolved fixture client to back it — degrade to not_connected
    // rather than fabricate a profile_id.
    return { state: "not_connected" };
  }
  return { state };
}

export type PersonExpansionData = {
  detail: ProPersonDetailResponse;
  activity: ProPersonActivityResponse;
};

// Expansion requests are intentionally contact-keyed and module-cached: opening
// a row is the only trigger, and collapsing/reopening never refetches it.
const peopleExpansionCache = new Map<string, PersonExpansionData>();

export function invalidatePersonExpansion(contactID: string): void {
  peopleExpansionCache.delete(contactID);
}

export function usePersonExpansion(
  contactID: string,
): AsyncResult<PersonExpansionData> {
  const loader = useCallback(
    async (getToken: GetProToken, signal: AbortSignal) => {
      const cached = peopleExpansionCache.get(contactID);
      if (cached) return cached;
      if (IS_PREVIEW) {
        const person = FIXTURE_PEOPLE.find(
          (candidate) => candidate.contactId === contactID,
        );
        if (!person) throw new Error("Person not found");
        const connectedClient =
          person.enrichmentState === "available"
            ? FIXTURE_CLIENTS.find((client) => client.name === person.name)
            : undefined;
        const data: PersonExpansionData = {
          detail: {
            person: {
              contact_id: person.contactId,
              first_name: person.name,
              last_name: "",
              source: "manual",
              tags: person.tags,
              area: person.area,
              timeline: person.timeline,
              relationship_stage: person.relationshipStage as never,
              sill_status: person.sillStatus,
              engagement: { attention: person.engagement.attention },
              capabilities: {
                can_invite: fixtureCapability(person.capabilities.canInvite),
                can_email: fixtureCapability(person.capabilities.canEmail),
                can_schedule: fixtureCapability(person.capabilities.canSchedule),
                can_task: fixtureCapability(person.capabilities.canTask),
                can_note: fixtureCapability(person.capabilities.canNote),
                can_in_app_message: fixtureCapability(person.capabilities.canInAppMessage),
                can_archive: fixtureCapability(false),
                can_restore: fixtureCapability(Boolean(person.capabilities.canRestore)),
                can_delete: fixtureCapability(false),
              },
              enrichment: fixtureEnrichment(person.enrichmentState, connectedClient),
            },
            notes: [],
            contact_note: "",
          },
          activity: { activity: [], unavailable_sources: [] },
        };
        peopleExpansionCache.set(contactID, data);
        return data;
      }
      const token = (await getToken()) ?? undefined;
      const [detail, activity] = await Promise.all([
        getJSON<ProPersonDetailResponse>(
          `/api/professional/people/${encodeURIComponent(contactID)}`,
          token,
          signal,
        ),
        getJSON<ProPersonActivityResponse>(
          `/api/professional/people/${encodeURIComponent(contactID)}/activity?limit=50`,
          token,
          signal,
        ),
      ]);
      const data = { detail, activity };
      peopleExpansionCache.set(contactID, data);
      return data;
    },
    [contactID],
  );
  return useAsync<PersonExpansionData>(`person-expansion:${contactID}`, loader);
}

/** Full Person-detail read. Unlike row expansion this is intentionally not
 * cached: the detail page owns mutations and must re-read canonical state. */
export function usePersonDetail(
  contactID: string | undefined,
): AsyncResult<PersonExpansionData> {
  const key = contactID ?? "";
  const loader = useCallback(
    async (getToken: GetProToken, signal: AbortSignal) => {
      if (!contactID) throw new Error("Missing person id");
      if (IS_PREVIEW) {
        await delay(PREVIEW_DELAY_MS, signal);
        const person = FIXTURE_PEOPLE.find(
          (candidate) => candidate.contactId === contactID,
        );
        if (!person) throw new Error("Person not found");
        const connectedClient =
          person.enrichmentState === "available"
            ? FIXTURE_CLIENTS.find((client) => client.name === person.name)
            : undefined;
        return {
          detail: {
            person: {
              contact_id: person.contactId,
              first_name: person.name,
              last_name: "",
              source: "manual",
              tags: person.tags,
              area: person.area,
              timeline: person.timeline,
              relationship_stage: person.relationshipStage as never,
              sill_status: person.sillStatus,
              engagement: { attention: person.engagement.attention },
              capabilities: {
                can_invite: fixtureCapability(person.capabilities.canInvite),
                can_email: fixtureCapability(person.capabilities.canEmail),
                can_schedule: fixtureCapability(person.capabilities.canSchedule),
                can_task: fixtureCapability(person.capabilities.canTask),
                can_note: fixtureCapability(person.capabilities.canNote),
                can_in_app_message: fixtureCapability(person.capabilities.canInAppMessage),
                can_archive: fixtureCapability(false),
                can_restore: fixtureCapability(Boolean(person.capabilities.canRestore)),
                can_delete: fixtureCapability(false),
              },
              enrichment: fixtureEnrichment(person.enrichmentState, connectedClient),
            },
            notes: [],
            contact_note: "",
          },
          activity: { activity: [], unavailable_sources: [] },
        } satisfies PersonExpansionData;
      }
      const token = (await getToken()) ?? undefined;
      const [detail, activity] = await Promise.all([
        getJSON<ProPersonDetailResponse>(
          `/api/professional/people/${encodeURIComponent(contactID)}`,
          token,
          signal,
        ),
        getJSON<ProPersonActivityResponse>(
          `/api/professional/people/${encodeURIComponent(contactID)}/activity?limit=50`,
          token,
          signal,
        ),
      ]);
      return { detail, activity };
    },
    [contactID],
  );
  return useAsync<PersonExpansionData>(`person-detail:${key}`, loader);
}

/** Cursor-owned activity state for the full Person page. It starts with the
 * detail read's first page, then appends distinct body-free events on demand. */
export function usePersonActivityLoadMore(
  contactID: string | undefined,
  initial: ProPersonActivityResponse | undefined,
) {
  const getToken = useProToken();
  const [activity, setActivity] = useState(initial?.activity ?? []);
  const [nextCursor, setNextCursor] = useState(initial?.next_cursor);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const initialRef = useRef(initial);

  useEffect(() => {
    if (initial !== initialRef.current) {
      initialRef.current = initial;
      setActivity(initial?.activity ?? []);
      setNextCursor(initial?.next_cursor);
      setError(null);
    }
  }, [initial]);

  const loadMore = useCallback(async () => {
    if (!contactID || !nextCursor || isLoading) return;
    setIsLoading(true);
    setError(null);
    try {
      const token = (await getToken()) ?? undefined;
      const page = await getJSON<ProPersonActivityResponse>(
        `/api/professional/people/${encodeURIComponent(contactID)}/activity?limit=50&cursor=${encodeURIComponent(nextCursor)}`,
        token,
      );
      setActivity((current) => {
        const seen = new Set(current.map((item) => item.event_key));
        return [
          ...current,
          ...(page.activity ?? []).filter((item) => !seen.has(item.event_key)),
        ];
      });
      setNextCursor(page.next_cursor);
    } catch (err) {
      setError(
        err instanceof Error ? err : new Error("Could not load activity"),
      );
    } finally {
      setIsLoading(false);
    }
  }, [contactID, getToken, isLoading, nextCursor]);

  return { activity, nextCursor, isLoading, error, loadMore };
}

export function usePersonNoteActions(
  contactID: string | undefined,
  afterUpdate?: () => void,
) {
  const getToken = useProToken();
  const request = useCallback(
    async (
      method: "POST" | "PATCH" | "DELETE",
      noteID?: string,
      body?: string,
    ) => {
      if (!contactID) throw new Error("Missing person id");
      if (IS_PREVIEW) {
        await previewDelay();
        afterUpdate?.();
        return;
      }
      const token = (await getToken()) ?? undefined;
      const path = `/api/professional/people/${encodeURIComponent(contactID)}/notes${noteID ? `/${encodeURIComponent(noteID)}` : ""}`;
      if (method === "POST") await postJSON(path, { body }, token);
      else if (method === "PATCH") await patchJSON(path, { body }, token);
      else await deleteJSON(path, token);
      afterUpdate?.();
    },
    [afterUpdate, contactID, getToken],
  );
  return useMemo(
    () => ({
      create: (body: string) => request("POST", undefined, body),
      update: (noteID: string, body: string) => request("PATCH", noteID, body),
      remove: (noteID: string) => request("DELETE", noteID),
    }),
    [request],
  );
}

// ─────────────────────────────────────────────────────────────────────────
// useClientDetail — one connected client's rich detail (plan 377 M5 addition
// — see backend/cmd/server/routes.go's GET /people/:contactId/client-detail
// registration comment). Contact-keyed now; the retired profile-keyed twin
// was GET /clients/:profileId (+ its sibling notes GET, folded into the
// already-existing GET /people/:contactId/notes below).
// ─────────────────────────────────────────────────────────────────────────

export function useClientDetail(
  contactId: string | undefined,
  scopeKey?: string,
  tokenResolver?: GetProToken,
): AsyncResult<ClientVM> {
  // A client-detail response may include the full connected-client VM. Keep
  // its async identity bound to the authenticated caller's scope when a
  // caller supplies one; a contact id alone is insufficient across actor or
  // role transitions on the same URL.
  // Supplying a scope makes this a protected, identity-bound read. An
  // unresolved actor/session must not fall back to a contact-only request.
  const identityBound = scopeKey !== undefined;
  const key = identityBound ? scopeKey ?? "" : contactId ?? "";
  const loader = useCallback(
    async (getToken: GetProToken, signal: AbortSignal) => {
      if (identityBound && !scopeKey) return null;
      if (!contactId) {
        throw new Error("Missing person id");
      }
      if (IS_PREVIEW) {
        await delay(PREVIEW_DELAY_MS, signal);
        const match = FIXTURE_CLIENTS.find(
          (c) => c.contactId === contactId || c.id === contactId,
        );
        if (!match) throw new Error("Client not found");
        return match;
      }
      const token = (await getToken()) ?? undefined;
      // A credential can settle after an actor/session transition. Do not let
      // that old credential start a protected request after its scoped load
      // has been cancelled.
      if (signal.aborted) return null;
      const [res, notes] = await Promise.all([
        getJSON<ProClientDetail>(
          `/api/professional/people/${contactId}/client-detail`,
          token,
          signal,
        ),
        getJSON<ProClientNotesResponse>(
          `/api/professional/people/${contactId}/notes`,
          token,
          signal,
        ),
      ]);
      return adaptClientDetail(res, notes.notes ?? []);
    },
    [contactId, identityBound, scopeKey],
  );
  const result = useAsync<ClientVM>(key, loader, tokenResolver);
  useDirtyResourceRefresh(
    contactId ? ["clients", `client:${contactId}`] : [],
    result.refresh,
  );
  return result;
}

// useClientTimelines / useClientNotes (SILL-367/379 roster-row prefetch
// hooks) were retired at plan 377 M5: their sole consumers were
// ClientListView/ClientRow, deleted at M4 (design spec §6.3 — superseded by
// PeopleRoster, whose own usePersonExpansion prefetch mechanism replaces
// them). Both hooks read from the retired /clients/:profileId[/notes]
// routes and had zero remaining production callers.

// ─────────────────────────────────────────────────────────────────────────
// useContacts — the book of business + per-page funnel summary.
//
// One loader returns BOTH the roster and the funnel breakdown (the wire
// envelope carries both), so the page renders them from a single async result
// without a second request.
// ─────────────────────────────────────────────────────────────────────────

export interface ContactsData {
  contacts: ContactVM[];
  funnel: ContactFunnelSummaryVM;
}

export interface ContactsQuery {
  /** substring over name/email */
  q?: string;
  funnel_state?: string;
  /**
   * OR-match: contacts with ANY of these tags. Widened from single `tag` (M3).
   * The backend accepts `tags=a&tags=b` (repeated params) or `tags=a,b` (CSV);
   * we append one param per tag for maximum explicitness.
   */
  tags?: string[];
  limit?: number;
  cursor?: string;
  /** When true, include archived contacts. Default false (archived excluded). */
  include_archived?: boolean;
}

// Plan 377 M5 (REST surface, design spec §4.1): GET /api/professional/contacts
// (the book-of-business roster this hook used to read) is retired — People
// (GET /people) is the one roster source now, covering every person
// (connected or not), matching the OLD contacts roster's scope. funnel_state
// and include_archived have no direct People-side equivalent and are
// intentionally dropped — no production useContacts() caller sets either
// (grep confirms every call site passes {}).
function buildPeopleContactsPath(query: ContactsQuery): string {
  const params = new URLSearchParams();
  if (query.q) params.set("query", query.q);
  for (const t of query.tags ?? []) {
    if (t) params.append("tag", t);
  }
  if (query.cursor) params.set("cursor", query.cursor);
  const qs = params.toString();
  return `/api/professional/people${qs ? `?${qs}` : ""}`;
}

function summarizeContacts(contacts: ContactVM[]): ContactFunnelSummaryVM {
  return contacts.reduce<ContactFunnelSummaryVM>(
    (summary, contact) => {
      if (contact.funnelState !== "unlinked") {
        summary[contact.funnelState] += 1;
      }
      return summary;
    },
    { added: 0, invited: 0, opened: 0, signed_up: 0, linked: 0 },
  );
}

export function useContacts(
  query: ContactsQuery = {},
): AsyncResult<ContactsData> {
  const key = JSON.stringify(query);
  const loader = useCallback(
    async (
      getToken: GetProToken,
      signal: AbortSignal,
    ): Promise<ContactsData> => {
      if (IS_PREVIEW) {
        await delay(PREVIEW_DELAY_MS, signal);
        return { contacts: FIXTURE_CONTACTS, funnel: FIXTURE_CONTACT_FUNNEL };
      }
      const pages = await loadCursorPages<
        ProPeopleListResponse,
        ContactsQuery
      >(JSON.parse(key) as ContactsQuery, buildPeopleContactsPath, getToken, signal);
      const contacts = pages.flatMap((res) =>
        (res.people ?? []).map((row) => personRowToContactVM(row)),
      );
      // People's list response carries `counts` (all/active/needs_me/...),
      // not the funnel_summary shape this hook's callers expect — always
      // derive the funnel breakdown client-side from the adapted rows.
      return { contacts, funnel: summarizeContacts(contacts) };
    },
    [key],
  );
  // A loaded-but-empty book still carries a funnel object, so override isEmpty
  // to mean "no contacts" specifically.
  const result = useAsync<ContactsData>(key, loader);
  useDirtyResourceRefresh(["contacts"], result.refresh);
  return {
    ...result,
    isEmpty:
      !result.isLoading &&
      !result.error &&
      (result.data?.contacts.length ?? 0) === 0,
  };
}

// ─────────────────────────────────────────────────────────────────────────
// useContact — single-person GET (for the drawer). Plan 377 M5 (REST
// surface, design spec §4.1): GET /api/professional/contacts/:id (this
// hook's old source) is retired — GET /people/:contactId is the one
// single-person read now, carrying the bio note (contact_note) as a
// detail-only sibling field (see backend proPersonDetailResponse's doc
// comment for why it can't live on the roster row).
// ─────────────────────────────────────────────────────────────────────────

export function useContact(
  contactId: string | undefined,
): AsyncResult<ContactVM> {
  const key = contactId ?? "";
  const loader = useCallback(
    async (getToken: GetProToken, signal: AbortSignal) => {
      if (!contactId) throw new Error("Missing contact id");
      if (IS_PREVIEW) {
        await delay(PREVIEW_DELAY_MS, signal);
        const match = FIXTURE_CONTACTS.find((c) => c.id === contactId);
        if (!match) throw new Error("Contact not found");
        return match;
      }
      const token = (await getToken()) ?? undefined;
      const res = await getJSON<ProPersonDetailResponse>(
        `/api/professional/people/${contactId}`,
        token,
        signal,
      );
      return personRowToContactVM(res.person, res.contact_note);
    },
    [contactId],
  );
  const result = useAsync<ContactVM>(key, loader);
  useDirtyResourceRefresh(
    contactId ? ["contacts", `contact:${contactId}`] : [],
    result.refresh,
  );
  return result;
}

// ─────────────────────────────────────────────────────────────────────────
// useContactTags — GET /api/professional/people/tags (plan 377 M5 — renamed
// from /contacts/tags, design spec §4.2; autocomplete list).
//
// Returns the distinct tag strings the pro has used across their book.
// The result is best-effort: an empty array on error is a graceful fallback
// (autocomplete just shows no suggestions).
// ─────────────────────────────────────────────────────────────────────────

export function useContactTags(): AsyncResult<string[]> {
  const loader = useCallback(
    async (getToken: GetProToken, signal: AbortSignal) => {
      if (IS_PREVIEW) {
        await delay(PREVIEW_DELAY_MS, signal);
        // Derive distinct tags from fixtures so the tag editor autocomplete
        // shows something realistic in preview/dev.
        const seen = new Set<string>();
        for (const c of FIXTURE_CONTACTS) {
          for (const t of c.tags) seen.add(t);
        }
        return Array.from(seen).sort();
      }
      const token = (await getToken()) ?? undefined;
      const res = await getJSON<{ tags: string[] }>(
        // Plan 377 M5 (REST surface, design spec §4.2) — renamed from
        // /contacts/tags.
        "/api/professional/people/tags",
        token,
        signal,
      );
      return res.tags ?? [];
    },
    [],
  );
  return useAsync<string[]>("contact-tags", loader);
}

// ─────────────────────────────────────────────────────────────────────────
// useContactCount — cheap, Tier-0-safe count of the pro's current
// non-deleted, non-archived contacts. Backs the WelcomePage first-run
// checklist's real "added your first contact" checkmark — the loading/error
// transient states just render the row undone, which matches the row's
// pre-hook default.
//
// Plan 377 M5 (REST surface, design spec §4.1): GET /api/professional/contacts/count
// (this hook's old source) is retired — People's list `counts.all` (the same
// "everyone in the book" total the old count represented) is the source now.
// A minimal limit=1 page keeps the payload cheap; `counts` is present on
// every page regardless of page size.
// ─────────────────────────────────────────────────────────────────────────

export function useContactCount(options?: {
  /**
   * When false the loader resolves to null WITHOUT touching the network —
   * the first-run welcome probe passes this once its one-shot seen flag is
   * set, so a returning pro never pays a contacts/count request just to
   * re-decide a redirect that can never fire again. Fixed at mount
   * (useAsync deliberately ignores later loader-identity changes).
   */
  enabled?: boolean;
}): AsyncResult<number> {
  const enabled = options?.enabled ?? true;
  const loader = useCallback(
    async (getToken: GetProToken, signal: AbortSignal) => {
      if (!enabled) return null;
      if (IS_PREVIEW) {
        await delay(PREVIEW_DELAY_MS, signal);
        return previewQueryParam("contacts") === "some" ? 3 : 0;
      }
      const token = (await getToken()) ?? undefined;
      const res = await getJSON<ProPeopleListResponse>(
        "/api/professional/people?limit=1",
        token,
        signal,
      );
      return res.counts?.all ?? 0;
    },
    [enabled],
  );
  return useAsync<number>("contact-count", loader);
}

// ─────────────────────────────────────────────────────────────────────────
// Contact mutations — update (PATCH) and archive (POST .../archive).
// ─────────────────────────────────────────────────────────────────────────

/** Returns an imperative function that PATCHes a contact. Full-state PATCH. */
export function useUpdateContact(): (
  contactId: string,
  body: UpdateContactRequest,
) => Promise<ContactVM> {
  const getToken = useProToken();
  return useCallback(
    async (
      contactId: string,
      body: UpdateContactRequest,
    ): Promise<ContactVM> => {
      if (IS_PREVIEW) {
        await previewDelay();
        // Return a plausible updated contact vm using fixtures + body
        const match =
          FIXTURE_CONTACTS.find((c) => c.id === contactId) ??
          FIXTURE_CONTACTS[0];
        const name =
          `${body.first_name} ${body.last_name}`.trim() ||
          match?.name ||
          "Unnamed contact";
        return {
          ...match!,
          name,
          firstName: body.first_name,
          lastName: body.last_name,
          email: body.email || undefined,
          phone: body.phone || undefined,
          notes: body.notes,
          tags: body.tags,
          area: body.area,
          horizon: body.horizon,
        };
      }
      const token = (await getToken()) ?? undefined;
      const res = await patchJSON<ProContactDTO>(
        `/api/professional/people/${contactId}`,
        body,
        token,
      );
      return adaptContact(res);
    },
    [getToken],
  );
}

/** Returns an imperative function that archives or restores a contact. */
export function useArchiveContact(): (
  contactId: string,
  archived: boolean,
) => Promise<void> {
  const getToken = useProToken();
  return useCallback(
    async (contactId: string, archived: boolean): Promise<void> => {
      if (IS_PREVIEW) {
        await previewDelay();
        return;
      }
      const token = (await getToken()) ?? undefined;
      const body: ArchiveContactRequest = { archived };
      await postJSON<void>(
        `/api/professional/people/${contactId}/archive`,
        body,
        token,
      );
    },
    [getToken],
  );
}

/** Deletes an eligible contact through the existing owner-scoped contact route. */
export function useDeleteContact(): (contactId: string) => Promise<void> {
  const getToken = useProToken();
  return useCallback(
    async (contactId: string) => {
      if (IS_PREVIEW) {
        await previewDelay();
        return;
      }
      const token = (await getToken()) ?? undefined;
      await deleteJSON(`/api/professional/people/${contactId}`, token);
    },
    [getToken],
  );
}

// ─────────────────────────────────────────────────────────────────────────
// useUpdateContactNote — M4 notes bridge.
//
// Writes the legacy compatibility note snapshot through PATCH
// /people/:contactId (plan 377 M5 — renamed from /contacts/:id, design spec
// §4.2). The API maps a nonempty value to one contact-keyed Person note and
// an empty value to its clear operation, so this caller sends only the
// field it owns.
// ─────────────────────────────────────────────────────────────────────────

export function useUpdateContactNote(
  afterUpdate?: () => void,
): (contactId: string, note: string) => Promise<void> {
  const getToken = useProToken();
  return useCallback(
    async (contactId: string, note: string): Promise<void> => {
      if (IS_PREVIEW) {
        await previewDelay();
        afterUpdate?.();
        return;
      }
      const token = (await getToken()) ?? undefined;
      const body: Partial<UpdateContactRequest> = { notes: note };
      await patchJSON<ProContactDTO>(
        `/api/professional/people/${contactId}`,
        body,
        token,
      );
      afterUpdate?.();
    },
    [afterUpdate, getToken],
  );
}

// ─────────────────────────────────────────────────────────────────────────
// useContactStageActions — M8-B relationship stage control (plan 291, D8
// rev 4). POST-only (throw on failure, resolve void on success) — the
// caller wraps this in a try/finally that re-fetches the authoritative
// stage regardless of outcome, so a 409 stale transition leaves the UI
// resynced by the time StageControl's catch block renders its "refreshed"
// notice. See PersonDetailPage for the wiring.
//
// Plan 377 M5 (REST surface, design spec §4.4): the duplicate profile-keyed
// POST /clients/:profileId/stage write (and its useClientStageActions
// caller — zero production callers, confirmed by grep) is retired. POST
// /people/:contactId/stage (the survivor, renamed from
// /contacts/:id/stage) is the ONE stage-write route now.
// ─────────────────────────────────────────────────────────────────────────

export interface StageActions {
  advance: (id: string, from: Stage, to: Stage) => Promise<void>;
  /** reason MUST be a RevertReasonCode (lib/stage.ts); note is optional
   * free text confined server-side to the audit trail (ADR-030 D18). */
  revert: (
    id: string,
    from: Stage,
    to: Stage,
    reason: RevertReasonCode,
    note: string,
  ) => Promise<void>;
}

function stagePath(id: string): string {
  return `/api/professional/people/${id}/stage`;
}

/** Contact-scoped stage actions: `id` is the crm_contacts row id. */
export function useContactStageActions(): StageActions {
  const getToken = useProToken();
  const advance = useCallback(
    async (id: string, from: Stage, to: Stage): Promise<void> => {
      if (IS_PREVIEW) {
        await previewDelay();
        return;
      }
      const token = (await getToken()) ?? undefined;
      await postJSON<StageOutcome>(
        stagePath(id),
        {
          action: "advance",
          from_stage: from,
          to_stage: to,
        } satisfies StageActionRequest,
        token,
      );
    },
    [getToken],
  );
  const revert = useCallback(
    async (
      id: string,
      from: Stage,
      to: Stage,
      reason: RevertReasonCode,
      note: string,
    ): Promise<void> => {
      if (IS_PREVIEW) {
        await previewDelay();
        return;
      }
      const token = (await getToken()) ?? undefined;
      await postJSON<StageOutcome>(
        stagePath(id),
        {
          action: "revert",
          from_stage: from,
          to_stage: to,
          reason,
          note: note || undefined,
        } satisfies StageActionRequest,
        token,
      );
    },
    [getToken],
  );
  return { advance, revert };
}

// ─────────────────────────────────────────────────────────────────────────
// useRecordAgreement — plan 291 round-3 "Record agreement" affordance.
// POSTs the marketplace agreement-attestation endpoint (D10: action="signed"
// is the ONE connected->agreement stage-transition trigger). Distinct from
// useClientStageActions — this call is keyed by the marketplace CONNECTION
// id, not the consumer profile id, and the caller is responsible for
// resyncing the client detail (which re-derives `stage`/`connectionId`)
// after either action, mirroring the stage-action hooks' resync contract.
// ─────────────────────────────────────────────────────────────────────────

export function useRecordAgreement(): (
  connectionId: string,
  action: "sent" | "signed",
) => Promise<AgreementActionResponse> {
  const getToken = useProToken();
  return useCallback(
    async (
      connectionId: string,
      action: "sent" | "signed",
    ): Promise<AgreementActionResponse> => {
      if (IS_PREVIEW) {
        await previewDelay();
        const now = new Date().toISOString();
        return {
          agreement:
            action === "signed"
              ? { signed_at: now, executed_via: "brokerage_tooling" }
              : { sent_at: now, executed_via: "brokerage_tooling" },
        };
      }
      const token = (await getToken()) ?? undefined;
      return postJSON<AgreementActionResponse>(
        `/api/marketplace/pro/connections/${connectionId}/agreement`,
        { action } satisfies AgreementActionRequest,
        token,
      );
    },
    [getToken],
  );
}

// useGetContact (imperative single-contact GET, formerly used by the M8-B
// stage-resync wrapper on the deleted ContactsView) was retired at plan 377
// M5 — its sole caller was deleted at M4 (design spec §6.3) and it read
// from the retired GET /contacts/:id.

// ─────────────────────────────────────────────────────────────────────────
// useActivityFeed — the cross-client event feed (the /activity surface).
// ─────────────────────────────────────────────────────────────────────────

export interface ActivityFeedQuery {
  /** keep only this event_type */
  type?: string;
  /** RFC3339 lower bound */
  since?: string;
  /**
   * Relative lower bound, in days before "now". Resolved to an absolute `since`
   * at FETCH time (inside the effect-driven loader), not during render — so a
   * caller can bound the feed to "the last N days" without a render-time
   * `Date.now()` (which the query key would also churn on). Ignored when an
   * absolute `since` is supplied.
   */
  withinDays?: number;
  limit?: number;
  cursor?: string;
}

function buildEventsPath(query: ActivityFeedQuery): string {
  const params = new URLSearchParams();
  if (query.type) params.set("type", query.type);
  if (query.since) params.set("since", query.since);
  if (query.limit) params.set("limit", String(query.limit));
  if (query.cursor) params.set("cursor", query.cursor);
  const qs = params.toString();
  return `/api/professional/events${qs ? `?${qs}` : ""}`;
}

/**
 * Resolve a relative `withinDays` window to an absolute RFC3339 `since`. Called
 * only from the loader (an effect), so the `Date.now()` read is outside render.
 */
function resolveSince(query: ActivityFeedQuery): ActivityFeedQuery {
  const { withinDays, ...rest } = query;
  if (rest.since || !withinDays) return rest;
  return {
    ...rest,
    since: new Date(
      Date.now() - withinDays * 24 * 60 * 60 * 1000,
    ).toISOString(),
  };
}

export function useActivityFeed(
  query: ActivityFeedQuery = {},
): AsyncResult<ActivityFeedItem[]> {
  const key = JSON.stringify(query);
  const loader = useCallback(
    async (getToken: GetProToken, signal: AbortSignal) => {
      if (IS_PREVIEW) {
        await delay(PREVIEW_DELAY_MS, signal);
        const q = JSON.parse(key) as ActivityFeedQuery;
        const all = FIXTURE_ACTIVITY_FEED;
        return q.type ? all.filter((e) => e.type === q.type) : all;
      }
      const pages = await loadCursorPages<ProEventsResponse, ActivityFeedQuery>(
        resolveSince(JSON.parse(key) as ActivityFeedQuery),
        buildEventsPath,
        getToken,
        signal,
      );
      return pages.flatMap((res) =>
        (res.events ?? []).map((e: ProActivityEvent) => adaptFeedItem(e)),
      );
    },
    [key],
  );
  return useAsync<ActivityFeedItem[]>(key, loader);
}

// ─────────────────────────────────────────────────────────────────────────
// useProfile — the pro's own profile + founding status (the /profile surface).
//
// Backed by the shared `useProfileStore` (SILL-346 follow-up item 3) so the
// auth-gate probe, HeaderNav, Settings, and Profile page all reuse ONE fetch
// instead of refetching per mount. This stays a thin selector over the store —
// the load/dedupe/error logic lives there — and preserves the AsyncResult shape
// so existing call sites are unchanged.
// ─────────────────────────────────────────────────────────────────────────

export function useProfile(): AsyncResult<ProProfileVM> {
  const getToken = useProToken();
  const data = useProfileStore((s) => s.data);
  const error = useProfileStore((s) => s.error);
  const hasLoaded = useProfileStore((s) => s.hasLoaded);

  useEffect(() => {
    void useProfileStore.getState().ensureLoaded(getToken);
  }, [getToken]);

  const refresh = useCallback(() => {
    return useProfileStore.getState().refresh(getToken);
  }, [getToken]);

  return {
    data,
    // First paint stays "loading" until the first fetch settles, so a page that
    // branches isLoading→loading / !data→empty never flashes the empty state.
    isLoading: !hasLoaded && !error,
    // The profile store dedupes loads itself; refreshes keep data mounted, so
    // there is no in-flight-with-data window to surface here.
    isRefreshing: false,
    error: error ? errorMessage(error) : null,
    errorCode: error instanceof ApiError ? (error.code ?? null) : null,
    isEmpty: hasLoaded && !error && data === null,
    refresh,
  };
}

// ─────────────────────────────────────────────────────────────────────────
// useNotificationPrefs — the /settings surface (digest + alert switches).
// ─────────────────────────────────────────────────────────────────────────

export function useNotificationPrefs(): AsyncResult<NotificationPrefVM[]> & {
  emailDeliveryBlocked: boolean;
} {
  const loader = useCallback(
    async (getToken: GetProToken, signal: AbortSignal) => {
      if (IS_PREVIEW) {
        await delay(PREVIEW_DELAY_MS, signal);
        return {
          preferences: FIXTURE_NOTIFICATION_PREFS,
          emailDeliveryBlocked: false,
        };
      }
      const token = (await getToken()) ?? undefined;
      const res = await getJSON<NotificationPrefsResponse>(
        "/api/notification-prefs",
        token,
        signal,
      );
      return {
        preferences: adaptNotificationPrefs(res.preferences ?? []),
        emailDeliveryBlocked: res.email_delivery_blocked ?? false,
      };
    },
    [],
  );
  const result = useAsync("notification-prefs", loader);
  return {
    ...result,
    data: result.data?.preferences ?? null,
    emailDeliveryBlocked: result.data?.emailDeliveryBlocked ?? false,
  };
}

// ─────────────────────────────────────────────────────────────────────────
// useMyReferrals / useMintProReferralLink — D16 CRM-month referral surface.
// ─────────────────────────────────────────────────────────────────────────

export function useMyReferrals(): AsyncResult<MyReferralsResponse> {
  const loader = useCallback(
    async (getToken: GetProToken, signal: AbortSignal) => {
      if (IS_PREVIEW) {
        await delay(PREVIEW_DELAY_MS, signal);
        const variant = previewQueryParam("referrals");
        if (variant === "error") {
          throw new Error("Couldn't load your CRM month rewards.");
        }
        if (variant === "empty") {
          return { links: [], rewards: [], waitlist_position: null };
        }
        return FIXTURE_PRO_REFERRALS;
      }
      const token = (await getToken()) ?? undefined;
      return getJSON<MyReferralsResponse>("/api/referrals/me", token, signal);
    },
    [],
  );
  return useAsync<MyReferralsResponse>("my-referrals", loader);
}

export function useMintProReferralLink(): () => Promise<
  MyReferralsResponse["links"][number]
> {
  const getToken = useProToken();
  return useCallback(async () => {
    if (IS_PREVIEW) {
      await previewDelay();
      return {
        token: "preview-pro-referral",
        kind: "pro_referral",
        created_at: new Date().toISOString(),
      };
    }
    const token = (await getToken()) ?? undefined;
    return postJSON<MyReferralsResponse["links"][number]>(
      "/api/referrals/links",
      { kind: "pro_referral" },
      token,
    );
  }, [getToken]);
}

// ─────────────────────────────────────────────────────────────────────────
// useBilling / useRateCard — marketplace payments (M6-A), the pro Settings
// Billing section.
// ─────────────────────────────────────────────────────────────────────────

export function useBilling(): AsyncResult<BillingStatusVM> {
  const loader = useCallback(
    async (getToken: GetProToken, signal: AbortSignal) => {
      if (IS_PREVIEW) {
        await delay(PREVIEW_DELAY_MS, signal);
        // e2e fixture-variant seam (marketplace-billing.spec.ts): forces the
        // preview into card-on-file / expired-card states via
        // `?billing=card|expired`. Absent or unrecognized → the no-card
        // default (the "more representative preview default").
        const variant = previewQueryParam("billing");
        if (variant === "card") return FIXTURE_BILLING_STATUS_CARD_ON_FILE;
        if (variant === "expired") return FIXTURE_BILLING_STATUS_EXPIRED;
        return FIXTURE_BILLING_STATUS;
      }
      const token = (await getToken()) ?? undefined;
      const res = await getJSON<BillingStatus>(
        "/api/marketplace/pro/billing",
        token,
        signal,
      );
      return adaptBillingStatus(res);
    },
    [],
  );
  return useAsync<BillingStatusVM>("billing", loader);
}

/** CRM subscription truth is separate from marketplace card-on-file state. */
export function useCRMBilling(): AsyncResult<CRMBillingSummaryVM> {
  const loader = useCallback(
    async (getToken: GetProToken, signal: AbortSignal) => {
      if (IS_PREVIEW) {
        await delay(PREVIEW_DELAY_MS, signal);
        // `?crmbilling=free|pro|past_due|canceled|cancel_at_period_end` — a
        // preview-only variant seam (mirrors `?billing=card|expired` on
        // useBilling above) so the catalog-driven pricing + every
        // subscription state (including the M4 lifecycle states) can be
        // exercised without a backend. Absent/unrecognized falls back to
        // "free".
        const variant = previewQueryParam("crmbilling");
        switch (variant) {
          case "pro":
            return FIXTURE_CRM_BILLING_PRO;
          case "past_due":
            return FIXTURE_CRM_BILLING_PAST_DUE;
          case "canceled":
            return FIXTURE_CRM_BILLING_CANCELED;
          case "cancel_at_period_end":
            return FIXTURE_CRM_BILLING_CANCEL_AT_PERIOD_END;
          default:
            return FIXTURE_CRM_BILLING_FREE;
        }
      }
      const token = (await getToken()) ?? undefined;
      const res = await getJSON<{
        plan: CRMBillingSummaryVM["plan"];
        status?: string;
        current_period_end?: string;
        cancel_at_period_end: boolean;
        max_clients: number;
        ai: boolean;
        ai_daily_cap: number;
        catalog?: {
          monthly_cents: number;
          annual_cents: number;
          founding_discount_percent: number;
          founding_discount_months: number;
        };
        // No `?` on the nested fields — the backend never omits them on a
        // present `offer` block (see CRMBillingOfferVM's doc); `offer` itself
        // stays optional to cover an older/pre-rollout response.
        offer?: {
          discount_percent: number;
          discount_months: number;
          free_months: number;
        };
        starter_promised_cents?: number;
        founding_discount_months_remaining?: number;
        activation?: {
          discount_active: boolean;
          discount_percent: number;
          discount_months: number;
          effective_monthly_cents: number;
          effective_annual_cents: number;
          annual_due_today_cents: number;
          starter_covers_months: number;
          starter_has_remainder: boolean;
        };
      }>("/api/professional/billing", token, signal);
      return {
        plan: res.plan,
        status: res.status ?? "",
        currentPeriodEnd: res.current_period_end ?? null,
        cancelAtPeriodEnd: res.cancel_at_period_end,
        maxClients: res.max_clients,
        ai: res.ai,
        aiDailyCap: res.ai_daily_cap,
        catalog: res.catalog
          ? {
              monthlyCents: res.catalog.monthly_cents,
              annualCents: res.catalog.annual_cents,
              foundingDiscountPercent: res.catalog.founding_discount_percent,
              foundingDiscountMonths: res.catalog.founding_discount_months,
            }
          : null,
        offer: res.offer
          ? {
              discountPercent: res.offer.discount_percent,
              discountMonths: res.offer.discount_months,
              freeMonths: res.offer.free_months,
            }
          : null,
        starterPromisedCents: res.starter_promised_cents ?? 0,
        // Backend sends this with no omitempty; ?? 0 only covers a stale
        // client mid-deploy skew, never a real "unknown" case — see the
        // field doc on CRMBillingSummaryVM.
        foundingDiscountMonthsRemaining:
          res.founding_discount_months_remaining ?? 0,
        // The backend's RESOLVED money picture. Mapped straight through with
        // no arithmetic — recomputing any of it here would be the second
        // implementation this block exists to remove. `null` only on a
        // response that predates the block; `resolveActivation` owns that
        // degrade.
        activation: res.activation
          ? {
              discountActive: res.activation.discount_active,
              discountPercent: res.activation.discount_percent,
              discountMonths: res.activation.discount_months,
              effectiveMonthlyCents: res.activation.effective_monthly_cents,
              effectiveAnnualCents: res.activation.effective_annual_cents,
              annualDueTodayCents: res.activation.annual_due_today_cents,
              starterCoversMonths: res.activation.starter_covers_months,
              starterHasRemainder: res.activation.starter_has_remainder,
            }
          : null,
      };
    },
    [],
  );
  return useAsync<CRMBillingSummaryVM>("crm-billing", loader);
}

export function useRateCard(
  opts: { enabled?: boolean } = {},
): AsyncResult<RateCardRowVM[]> {
  const enabled = opts.enabled ?? true;
  const loader = useCallback(
    async (getToken: GetProToken, signal: AbortSignal) => {
      // Skipped (no request) while the caller's marketplace gate is not `on`.
      if (!enabled) return [] as RateCardRowVM[];
      if (IS_PREVIEW) {
        await delay(PREVIEW_DELAY_MS, signal);
        return FIXTURE_RATE_CARD;
      }
      const token = (await getToken()) ?? undefined;
      const res = await getJSON<ProRateCardResponse>(
        "/api/marketplace/pro/rate-card",
        token,
        signal,
      );
      return adaptRateCard(res.rates ?? []);
    },
    [enabled],
  );
  return useAsync<RateCardRowVM[]>(enabled ? "rate-card" : "rate-card:off", loader);
}

/**
 * SILL-521 M2 cluster 4 — the copilot's daily message allowance. Feeds the
 * free-tier remaining-turns line + post-exhaustion composer swap in
 * ChatDockPanel; `pro` tier renders nothing new (three-surface rule).
 */
export function useAssistantAllowance(): AsyncResult<AssistantAllowanceVM> {
  const loader = useCallback(
    async (getToken: GetProToken, signal: AbortSignal) => {
      if (IS_PREVIEW) {
        await delay(PREVIEW_DELAY_MS, signal);
        // `?allowance=free|free_exhausted|pro` — mirrors the `?crmbilling=`
        // preview-variant seam. Absent/unrecognized falls back to "free".
        const variant = previewQueryParam("allowance");
        switch (variant) {
          case "free_exhausted":
            return FIXTURE_ASSISTANT_ALLOWANCE_FREE_EXHAUSTED;
          case "pro":
            return FIXTURE_ASSISTANT_ALLOWANCE_PRO;
          default:
            return FIXTURE_ASSISTANT_ALLOWANCE_FREE;
        }
      }
      const token = (await getToken()) ?? undefined;
      return getJSON<AssistantAllowanceVM>(
        "/api/professional/assistant/allowance",
        token,
        signal,
      );
    },
    [],
  );
  return useAsync<AssistantAllowanceVM>("assistant-allowance", loader);
}

// ─────────────────────────────────────────────────────────────────────────
// Mutations. Each returns an imperative async fn that branches on IS_PREVIEW:
// in preview it simulates a plausible result; in prod it calls the real
// endpoint with a Clerk token. The token resolver is the same single hook.
// ─────────────────────────────────────────────────────────────────────────

export interface ProMutations {
  /** Cursor-walk the canonical People endpoint to freeze explicit contact IDs. */
  freezePeopleInviteCandidates: (
    query: URLSearchParams,
  ) => Promise<{ ids: string[]; truncated: boolean }>;
  /** Manual add (POST /people, plan 377 M5 — renamed from /contacts). Returns the new contact view model. */
  addContact: (input: CreateContactRequest) => Promise<ContactVM>;
  /** Upload a CSV (POST /people/imports, plan 377 M5 — renamed from /contact-imports) → preview + suggested mapping. */
  uploadImport: (file: File) => Promise<ImportPreviewVM>;
  /** Commit a staged import (POST /people/imports/:id/commit, plan 377 M5 — renamed from /contact-imports/:id/commit). */
  commitImport: (
    batchId: string,
    mapping: CommitMappingBody,
  ) => Promise<ImportResultVM>;
  /** Mass-invite (POST /invites). Returns aggregate counts (no per-email). */
  sendInvites: (
    contactIds: string[],
    message: string,
  ) => Promise<InviteResultVM>;
  /**
   * Batch invite preflight (POST /invites/preflight). Returns six eligibility
   * buckets + the frozen eligible id set the FE holds for dispatch.
   * IncludeRenudges=false by default (pass explicit ids OR active filter).
   */
  preflightInvites: (opts: {
    ids?: string[];
    query?: string;
    tags?: string[];
    includeRenudges?: boolean;
  }) => Promise<PreflightResultVM>;
  /**
   * Start a batch invite job (POST /invites/batch). Returns immediately with
   * a job_id. The FE polls GET /invites/batch/:job_id for progress.
   */
  startBatchInvite: (opts: {
    ids: string[];
    includeRenudges?: boolean;
    jobKey?: string;
    message?: string;
  }) => Promise<string>;
  /** Poll a batch invite job (GET /invites/batch/:job_id). */
  getBatchInviteJob: (jobId: string) => Promise<BatchJobVM>;
  /** Edit the pro profile (PATCH /profile). */
  updateProfile: (edits: UpdateProProfileRequest) => Promise<ProProfileVM>;
  /** Upsert notification prefs (PUT /notification-prefs). */
  updateNotificationPrefs: (
    prefs: NotificationPref[],
  ) => Promise<NotificationPrefVM[]>;
  /**
   * Create a Stripe SetupIntent (POST /billing/setup-intent) and return the
   * opaque client_secret. Pure fetch/adapt — no @stripe import here; mounting
   * the Payment Element from this secret is a later step.
   */
  createSetupIntent: () => Promise<string>;
  /** Verify and persist a client-confirmed SetupIntent for the caller. */
  confirmSetupIntent: (setupIntentId: string) => Promise<void>;
  createCRMCheckout: (interval: "monthly" | "annual") => Promise<string>;
  /**
   * Release the caller's own checkout reservation (POST
   * /billing/checkout/cancel) after an abandoned Stripe Checkout page, so a
   * retry doesn't 409 for the rest of the 35-minute lease. No-op server-side
   * while a live subscription exists.
   */
  cancelCRMCheckout: () => Promise<void>;
  openCRMBillingPortal: () => Promise<string>;
}

export function useProMutations(): ProMutations {
  const previewPrefs = useRef(FIXTURE_NOTIFICATION_PREFS);
  const getToken = useProToken();

  const addContact = useCallback(
    async (input: CreateContactRequest): Promise<ContactVM> => {
      if (IS_PREVIEW) {
        await previewDelay();
        const name =
          `${input.first_name ?? ""} ${input.last_name ?? ""}`.trim() ||
          input.email?.split("@")[0] ||
          "Unnamed contact";
        return {
          id: `c-new-${Date.now()}`,
          name,
          avatar: initialsOf(name),
          email: input.email,
          phone: input.phone,
          firstName: input.first_name ?? "",
          lastName: input.last_name ?? "",
          notes: input.notes ?? "",
          tags: input.tags ?? [],
          source: "manual",
          funnelState: "added",
          added: "Added just now",
          area: "",
          horizon: "",
          archivedAt: null,
        };
      }
      const token = (await getToken()) ?? undefined;
      // Plan 377 M5 (REST surface, design spec §4.2) — renamed from
      // POST /contacts.
      const res = await postJSON<ProContactDTO>(
        "/api/professional/people",
        input,
        token,
      );
      return adaptContact(res);
    },
    [getToken],
  );

  const uploadImport = useCallback(
    async (file: File): Promise<ImportPreviewVM> => {
      if (IS_PREVIEW) {
        await previewDelay();
        return { ...FIXTURE_IMPORT_PREVIEW, filename: file.name };
      }
      const token = (await getToken()) ?? undefined;
      const form = new FormData();
      form.append("file", file);
      const res = await apiRequest<ImportPreview>(
        "/api/professional/people/imports",
        { method: "POST", body: form, token },
      );
      return adaptImportPreview(res);
    },
    [getToken],
  );

  const commitImport = useCallback(
    async (
      batchId: string,
      mapping: CommitMappingBody,
    ): Promise<ImportResultVM> => {
      if (IS_PREVIEW) {
        await previewDelay();
        // A plausible split of the fixture's 42 rows.
        return {
          imported: 38,
          skippedDuplicate: 3,
          invalid: 1,
          importedContactIds: FIXTURE_CONTACTS.slice(0, 5).map((c) => c.id),
        };
      }
      const token = (await getToken()) ?? undefined;
      const res = await postJSON<ImportCommitResult>(
        `/api/professional/people/imports/${batchId}/commit`,
        { mapping },
        token,
      );
      return adaptImportResult(res);
    },
    [getToken],
  );

  const sendInvites = useCallback(
    async (contactIds: string[], message: string): Promise<InviteResultVM> => {
      if (IS_PREVIEW) {
        await previewDelay();
        // Plausible counts; a suppressed recipient is folded into `sent` (privacy
        // — there is no separate suppressed count to expose).
        return {
          sent: contactIds.length,
          skippedAlreadySent: 0,
          skippedCooldown: 0,
          invalid: 0,
          skippedRateLimited: 0,
        };
      }
      const token = (await getToken()) ?? undefined;
      const res = await postJSON<InviteSendResult>(
        "/api/professional/invites",
        { contact_ids: contactIds, message },
        token,
      );
      return adaptInviteResult(res);
    },
    [getToken],
  );

  const preflightInvites = useCallback(
    async (opts: {
      ids?: string[];
      query?: string;
      tags?: string[];
      includeRenudges?: boolean;
    }): Promise<PreflightResultVM> => {
      if (IS_PREVIEW) {
        await previewDelay();
        // Preview: treat all ids as eligible.
        return {
          eligible: opts.ids ?? [],
          alreadyInvited: [],
          signedUp: [],
          linked: [],
          noEmail: [],
          suppressed: [],
        };
      }
      const token = (await getToken()) ?? undefined;
      const res = await postJSON<InvitePreflightResult>(
        "/api/professional/invites/preflight",
        {
          ids: opts.ids ?? [],
          query: opts.query ?? "",
          tags: opts.tags ?? [],
          include_renudges: opts.includeRenudges ?? false,
        },
        token,
      );
      return {
        eligible: res.eligible ?? [],
        alreadyInvited: res.already_invited ?? [],
        signedUp: res.signed_up ?? [],
        linked: res.linked ?? [],
        noEmail: res.no_email ?? [],
        suppressed: res.suppressed ?? [],
        truncated: res.truncated,
      };
    },
    [getToken],
  );

  const freezePeopleInviteCandidates = useCallback(
    async (query: URLSearchParams) => {
      if (IS_PREVIEW) {
        await previewDelay();
        return {
          ids: FIXTURE_PEOPLE.map((person) => person.contactId),
          truncated: false,
        };
      }
      const token = (await getToken()) ?? undefined;
      const ids: string[] = [];
      let cursor: string | undefined;
      do {
        const params = new URLSearchParams(query);
        params.set("limit", "200");
        if (cursor) params.set("cursor", cursor);
        const page = await getJSON<ProPeopleListResponse>(
          `/api/professional/people?${params}`,
          token,
        );
        for (const person of page.people ?? []) {
          if (ids.length === 1000) return { ids, truncated: true };
          ids.push(person.contact_id);
        }
        cursor = page.next_cursor;
      } while (cursor);
      return { ids, truncated: false };
    },
    [getToken],
  );

  const startBatchInvite = useCallback(
    async (opts: {
      ids: string[];
      includeRenudges?: boolean;
      jobKey?: string;
      message?: string;
    }): Promise<string> => {
      if (IS_PREVIEW) {
        await previewDelay();
        return `preview-job-${Date.now()}`;
      }
      const token = (await getToken()) ?? undefined;
      const res = await postJSON<BatchInviteStartResult>(
        "/api/professional/invites/batch",
        {
          ids: opts.ids,
          include_renudges: opts.includeRenudges ?? false,
          job_key: opts.jobKey,
          message: opts.message ?? "",
        },
        token,
      );
      return res.job_id;
    },
    [getToken],
  );

  const getBatchInviteJob = useCallback(
    async (jobId: string): Promise<BatchJobVM> => {
      if (IS_PREVIEW) {
        await previewDelay();
        return {
          jobId,
          status: "done",
          total: 5,
          sent: 5,
          failed: [],
        };
      }
      const token = (await getToken()) ?? undefined;
      const res = await getJSON<BatchInviteJobStatus>(
        `/api/professional/invites/batch/${jobId}`,
        token,
      );
      const failed: BatchFailedEntryVM[] = (res.failed ?? []).map(
        (e: BatchInviteFailedEntry) => ({
          contactId: e.contact_id,
          reason: e.reason,
        }),
      );
      return {
        jobId,
        status: res.status,
        total: res.total,
        sent: res.sent,
        failed,
      };
    },
    [getToken],
  );

  const updateProfile = useCallback(
    async (edits: UpdateProProfileRequest): Promise<ProProfileVM> => {
      if (IS_PREVIEW) {
        await previewDelay();
        return {
          ...FIXTURE_PRO_PROFILE,
          bio: edits.bio ?? FIXTURE_PRO_PROFILE.bio,
          brokerage: edits.brokerage ?? FIXTURE_PRO_PROFILE.brokerage,
          directoryPublic:
            edits.directory_public ?? FIXTURE_PRO_PROFILE.directoryPublic,
          serviceAreas: edits.service_areas ?? FIXTURE_PRO_PROFILE.serviceAreas,
        };
      }
      const token = (await getToken()) ?? undefined;
      const res = await patchJSON<ProProfile>(
        "/api/professional/profile",
        edits,
        token,
      );
      return adaptProProfile(res);
    },
    [getToken],
  );

  const updateNotificationPrefs = useCallback(
    async (prefs: NotificationPref[]): Promise<NotificationPrefVM[]> => {
      if (IS_PREVIEW) {
        await previewDelay();
        previewPrefs.current = adaptNotificationPrefs([
          ...previewPrefs.current.map(({ channel, category, frequency }) => ({
            channel,
            category,
            frequency,
          })),
          ...prefs,
        ]);
        return previewPrefs.current;
      }
      const token = (await getToken()) ?? undefined;
      const res = await putJSON<NotificationPrefsResponse>(
        "/api/notification-prefs",
        { preferences: prefs },
        token,
      );
      return adaptNotificationPrefs(res.preferences ?? []);
    },
    [getToken],
  );

  const createSetupIntent = useCallback(async (): Promise<string> => {
    if (IS_PREVIEW) {
      await previewDelay();
      return `preview-seti-${Date.now()}-secret`;
    }
    const token = (await getToken()) ?? undefined;
    const res = await postJSON<SetupIntentResponse>(
      "/api/marketplace/pro/billing/setup-intent",
      {},
      token,
    );
    return res.client_secret;
  }, [getToken]);

  const confirmSetupIntent = useCallback(
    async (setupIntentId: string): Promise<void> => {
      if (IS_PREVIEW) {
        await previewDelay();
        return;
      }
      const token = (await getToken()) ?? undefined;
      await postJSON(
        "/api/marketplace/pro/billing/setup-intent/confirm",
        { setup_intent_id: setupIntentId },
        token,
      );
    },
    [getToken],
  );

  const createCRMCheckout = useCallback(
    async (interval: "monthly" | "annual") => {
      if (IS_PREVIEW) {
        await previewDelay();
        return `/settings/billing?billing=checkout-${interval}`;
      }
      const token = (await getToken()) ?? undefined;
      const result = await postJSON<{ url: string }>(
        "/api/professional/billing/checkout",
        { interval },
        token,
      );
      return result.url;
    },
    [getToken],
  );

  // Releases the caller's own checkout reservation after an abandoned Stripe
  // Checkout page (the `?billing=cancel` return, or a 409 on a fresh
  // attempt), so a retry doesn't 409 for the rest of the 35-minute lease.
  // The backend no-ops while a live subscription exists.
  const cancelCRMCheckout = useCallback(async (): Promise<void> => {
    if (IS_PREVIEW) {
      await previewDelay();
      return;
    }
    const token = (await getToken()) ?? undefined;
    await postJSON("/api/professional/billing/checkout/cancel", {}, token);
  }, [getToken]);

  const openCRMBillingPortal = useCallback(async () => {
    if (IS_PREVIEW) {
      await previewDelay();
      return "/settings/billing?billing=manage";
    }
    const token = (await getToken()) ?? undefined;
    const result = await postJSON<{ url: string }>(
      "/api/professional/billing/portal",
      {},
      token,
    );
    return result.url;
  }, [getToken]);

  return useMemo(
    () => ({
      addContact,
      uploadImport,
      commitImport,
      sendInvites,
      preflightInvites,
      freezePeopleInviteCandidates,
      startBatchInvite,
      getBatchInviteJob,
      updateProfile,
      updateNotificationPrefs,
      createSetupIntent,
      confirmSetupIntent,
      createCRMCheckout,
      cancelCRMCheckout,
      openCRMBillingPortal,
    }),
    [
      addContact,
      uploadImport,
      commitImport,
      sendInvites,
      preflightInvites,
      freezePeopleInviteCandidates,
      startBatchInvite,
      getBatchInviteJob,
      updateProfile,
      updateNotificationPrefs,
      createSetupIntent,
      confirmSetupIntent,
      createCRMCheckout,
      cancelCRMCheckout,
      openCRMBillingPortal,
    ],
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Right-rail content + KPI strip. Preview uses the hand-authored fixtures for
// design fidelity; PROD derives from the SAME live data the page already loads
// (SILL-353): KPIs from the roster (`useClients`), signals from the activity
// feed (`useActivityFeed` → /api/professional/events), and the Sill-suggests
// card from the top live signal. Derivation is pure (lib/derive.ts) so the
// prod behavior is unit-tested independent of these thin wrappers.
// ─────────────────────────────────────────────────────────────────────────

export function useSignalFeed(feed: ActivityFeedItem[]): SignalEvent[] {
  return useMemo(
    () => (IS_PREVIEW ? FIXTURE_SIGNALS : deriveSignals(feed)),
    [feed],
  );
}

export function useSillSuggestion(
  signals: SignalEvent[],
): SillSuggestion | null {
  return useMemo(
    () => (IS_PREVIEW ? FIXTURE_SUGGESTION : deriveSuggestion(signals)),
    [signals],
  );
}

export function useKpis(clients: ClientVM[]): KpiMetric[] {
  return useMemo(
    () => (IS_PREVIEW ? FIXTURE_KPIS : deriveKpis(clients)),
    [clients],
  );
}

/**
 * The logged-in pro identity. Name/email/avatar come from Clerk (the source of
 * truth for identity); org/role come from the professional profile. Preview
 * returns the fixture directly — no Clerk provider and no fetch. `isLoading`
 * stays true until the profile resolves so the sidebar can skeleton the org/role
 * rather than flash an empty value (the Clerk name is available immediately).
 */
export function useProUser(): ProUserResult {
  const profile = useProfile();
  if (IS_PREVIEW) {
    return { user: FIXTURE_PRO, isLoading: false };
  }
  return composeProUser(readClerkIdentity(), profile.data, profile.isLoading);
}

// ─────────────────────────────────────────────────────────────────────────
// helpers
// ─────────────────────────────────────────────────────────────────────────

function delay(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const id = setTimeout(resolve, ms);
    signal.addEventListener(
      "abort",
      () => {
        clearTimeout(id);
        reject(new DOMException("aborted", "AbortError"));
      },
      { once: true },
    );
  });
}

/** Simple delay for mutation previews (no abort signal — these are imperative). */
function previewDelay(ms = PREVIEW_DELAY_MS): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Initials from a display name (max 2), e.g. "Nina Park" → "NP". */
function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "";
  return (first + last).toUpperCase() || "?";
}

function errorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  return "Something went wrong";
}
